// Brand of this build, chosen by REACT_APP_ORGANIZATION in the env file (one build per organization).
// "Adeed" builds ADEED Freight; anything else (or absent) builds TruKKer Partner.
import { theme } from "antd";
import TrukkerLogo from "../assets/images/trukker-logo.png";
import AdeedLogo from "../assets/images/adeed-wordmark-dark.png";
import { ThemeMode } from "./commonConstants";

export const Organization = {
  TRUKKER: "Trukker",
  ADEED: "Adeed",
};

const TrukkerTermsUrl = "https://trukker.com/tc.html";
const TrukkerPrivacyUrl = "https://trukker.com/privacy.html";

export const isAdeed =
  (process.env.REACT_APP_ORGANIZATION || "").trim().toLowerCase() === Organization.ADEED.toLowerCase();

export const Brand = isAdeed
  ? {
      organization: Organization.ADEED,
      appName: "ADEED Freight",
      androidStoreUrl: process.env.REACT_APP_ANDROID_STORE_URL?.trim() || null,
      iosStoreUrl: process.env.REACT_APP_IOS_STORE_URL?.trim() || null,
      termsUrl: process.env.REACT_APP_TERMS_URL?.trim() || TrukkerTermsUrl,
      privacyUrl: process.env.REACT_APP_PRIVACY_URL?.trim() || TrukkerPrivacyUrl,
      logo: AdeedLogo, // dark artwork, for light backgrounds
    }
  : {
      organization: Organization.TRUKKER,
      appName: "TruKKer Partner",
      androidStoreUrl: "https://play.google.com/store/apps/details?id=com.trukkeruae.trukker_vender",
      iosStoreUrl: "https://apps.apple.com/us/app/trukker-partner/id1624013412",
      termsUrl: TrukkerTermsUrl,
      privacyUrl: TrukkerPrivacyUrl,
      logo: TrukkerLogo,
    };

// Brand colours as JS values, for the few places that cannot read a CSS variable (Google Maps
// polylines, SVG/canvas props). Same values as the variables in src/index.css.
export const BrandColors = isAdeed
  ? {
      primary: "#0020F5",
      primaryHover: "#3355F0",
      primaryDark: "#001ACC",
      primaryTint: "#F0F2FF",
      tint: "#DDE2F8",
      primaryRgb: "0, 32, 245",
      attention: "#0020F5", // no orange or gold accent in ADEED: attention is the brand blue
      success: "#22A06B",
      error: "#DC2626",
      ink: "#0A0F1E",
      muted: "#5B6474",
      canvas: "#F4F5F9",
      line: "#E6E8EC",
      fontFamily: '"Space Grotesk", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
    }
  : {
      primary: "#FF833C",
      primaryHover: "#FF833C",
      primaryDark: "#E95D05",
      primaryTint: "#FFF1E8",
      tint: "#FAE2DB",
      primaryRgb: "255, 131, 60",
      attention: "#F99E00",
      success: "#19AB4F",
      error: "#DA1414",
      ink: "#000000",
      muted: "#757575",
      canvas: "#F5F5F5",
      line: "#D9D9D9",
      fontFamily: "Silka Regular",
    };

// Ant Design theme of this build, used by every ConfigProvider (App, AuthLayout, GuestLayout, PublicLayout).
// TruKKer keeps the tokens it always had; ADEED mirrors the ADEED portal: blue primary, navy ink,
// Space Grotesk, softer radii, a 4px brand focus ring, quiet tables and a grey active menu item.
export const antdTheme = isAdeed
  ? {
      token: {
        colorPrimary: BrandColors.primary,
        colorLink: BrandColors.primary,
        colorLinkHover: BrandColors.primaryHover,
        colorLinkActive: BrandColors.primaryDark,
        colorInfo: BrandColors.primary,
        colorSuccess: BrandColors.success,
        colorWarning: BrandColors.primary, // ADEED has no orange/amber: warnings are blue
        colorError: BrandColors.error,
        colorTextBase: BrandColors.ink,
        colorBgLayout: BrandColors.canvas,
        colorBorder: "#D5D9DF",
        colorBorderSecondary: BrandColors.line,
        fontFamily: BrandColors.fontFamily,
        borderRadius: 10,
        borderRadiusLG: 16,
        borderRadiusSM: 8,
        controlOutline: "rgba(51, 85, 240, 0.15)",
        controlOutlineWidth: 4,
        boxShadowTertiary: "0 1px 2px rgba(15, 23, 41, 0.04)",
      },
      components: {
        Button: {
          fontWeight: 500,
          primaryShadow: "0 8px 20px -10px rgba(0, 32, 245, 0.6)",
          defaultBorderColor: BrandColors.line,
          defaultColor: BrandColors.ink,
          defaultHoverBg: "#FAFBFB",
          defaultShadow: "none",
        },
        Input: {
          activeBorderColor: "#3355F0",
          hoverBorderColor: "#99AAF7",
        },
        Select: {
          optionSelectedBg: BrandColors.primaryTint,
          optionActiveBg: "#F7F8FA",
        },
        Table: {
          headerBg: "#F7F8FA",
          headerColor: BrandColors.muted,
          rowHoverBg: "#F7F8FA",
          borderColor: "#EEF0F2",
        },
        Tabs: {
          inkBarColor: BrandColors.primary,
          itemSelectedColor: BrandColors.primary,
          itemHoverColor: BrandColors.primaryHover,
          itemColor: BrandColors.muted,
        },
        Menu: {
          itemColor: "#3E4757",
          itemHoverColor: BrandColors.ink,
          itemHoverBg: "rgba(0, 0, 0, 0.03)",
          itemSelectedColor: BrandColors.ink,
          itemSelectedBg: "#F2F3F5",
          itemBorderRadius: 12,
        },
        Card: {
          borderRadiusLG: 24,
        },
        Modal: {
          borderRadiusLG: 20,
          titleFontSize: 18,
        },
      },
    }
  : {
      token: {
        fontFamily: BrandColors.fontFamily,
        colorPrimary: "#FF5D00",
      },
    };

// Lokalize is shared with the TruKKer apps, so its texts name the product "LoadBoard". The ADEED build shows
// its own name instead, whatever the language (the name stays in Latin script, as TruKKer's does).
const LoadboardNamePattern = /load\s*-?\s*board|\u0932\u094B\u0921\u092C\u094B\u0930\u094D\u0921/gi;

export const brandText = (value) =>
  isAdeed && typeof value === "string" ? value.replace(LoadboardNamePattern, Brand.appName) : value;

// ADEED dark mode (Settings > Appearance), the ADEED portal's dark palette (procureco src/index.css
// html[data-mode='dark'], tailwind.config.js): a near-black navy canvas, #121826 surfaces (cards, sidebar,
// sheets and menus alike), #1A2133 quiet fills and the active nav item, #263049 lines, #E7EBF3 ink with
// #A7B0C0 / #808A9D / #566075 greys, blue tints darkened (#121A40) and accent text in #96AAFF.
// Same values as :root[data-brand='adeed'][data-mode='dark'] in src/index.css.
export const BrandDarkColors = {
  primary: "#0020F5",
  primaryHover: "#3355F0",
  primaryActive: "#001ACC",
  accentText: "#96AAFF",
  accentTextHover: "#AFC0FF",
  accentTextActive: "#6680F4",
  linkSeed: "#AFC0FF", // antd's dark algorithm turns this seed into ~#98A6DC, the closest it gets to #96AAFF
  canvas: "#0A0E18",
  surface: "#121826",
  fill: "#1A2133",
  hover: "#1E2639",
  ink: "#E7EBF3",
  muted: "#A7B0C0",
  subtle: "#808A9D",
  faint: "#566075",
  placeholder: "#6B7589",
  line: "#263049",
  hair: "#1F2738",
  fieldBorder: "#33405C",
  fieldBorderHover: "#46506A",
  inkFill: "#2B3652",
  brandTint: "#121A40",
  brandTintStrong: "#18245C",
  brandTintBorder: "#283C96",
};

const adeedDarkAntdTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...antdTheme.token,
    colorPrimary: BrandDarkColors.primary,
    colorInfo: BrandDarkColors.primary,
    colorWarning: BrandDarkColors.primaryHover,
    colorLink: BrandDarkColors.linkSeed,
    colorLinkHover: BrandDarkColors.accentTextHover,
    colorLinkActive: BrandDarkColors.accentTextActive,
    colorPrimaryText: BrandDarkColors.accentText,
    colorPrimaryTextHover: BrandDarkColors.accentTextHover,
    colorPrimaryTextActive: BrandDarkColors.accentTextActive,
    colorPrimaryBg: BrandDarkColors.brandTint,
    colorPrimaryBgHover: BrandDarkColors.brandTintStrong,
    colorPrimaryBorder: BrandDarkColors.brandTintBorder,
    colorTextBase: BrandDarkColors.ink,
    colorText: BrandDarkColors.ink,
    colorTextHeading: BrandDarkColors.ink,
    colorTextSecondary: BrandDarkColors.muted,
    colorTextTertiary: BrandDarkColors.subtle,
    colorTextDescription: BrandDarkColors.subtle,
    colorTextQuaternary: BrandDarkColors.faint,
    colorTextPlaceholder: BrandDarkColors.placeholder,
    colorBgBase: BrandDarkColors.canvas,
    colorBgLayout: BrandDarkColors.canvas,
    colorBgContainer: BrandDarkColors.surface,
    colorBgElevated: BrandDarkColors.surface,
    colorBgSpotlight: BrandDarkColors.inkFill,
    colorBorder: BrandDarkColors.fieldBorder,
    colorBorderSecondary: BrandDarkColors.line,
    colorSplit: BrandDarkColors.line,
    boxShadowTertiary: "0 1px 2px rgba(0, 0, 0, 0.4)",
  },
  components: {
    ...antdTheme.components,
    Button: {
      ...antdTheme.components?.Button,
      // The portal's primary button keeps the ADEED blue in dark mode.
      colorPrimary: BrandDarkColors.primary,
      colorPrimaryHover: BrandDarkColors.primaryHover,
      colorPrimaryActive: BrandDarkColors.primaryActive,
      colorLink: BrandDarkColors.accentText,
      colorLinkHover: BrandDarkColors.accentTextHover,
      colorLinkActive: BrandDarkColors.accentTextActive,
      defaultBg: BrandDarkColors.surface,
      defaultBorderColor: BrandDarkColors.line,
      defaultColor: BrandDarkColors.ink,
      defaultHoverBg: BrandDarkColors.hover,
      defaultHoverBorderColor: BrandDarkColors.fieldBorder,
      defaultHoverColor: BrandDarkColors.ink,
    },
    Input: {
      activeBorderColor: BrandDarkColors.primaryHover,
      hoverBorderColor: BrandDarkColors.fieldBorderHover,
    },
    Select: {
      optionSelectedBg: BrandDarkColors.brandTint,
      optionActiveBg: BrandDarkColors.hover,
    },
    Table: {
      headerBg: BrandDarkColors.fill,
      headerColor: BrandDarkColors.muted,
      rowHoverBg: BrandDarkColors.hover,
      borderColor: BrandDarkColors.hair,
    },
    Tabs: {
      inkBarColor: BrandDarkColors.primaryHover,
      itemSelectedColor: BrandDarkColors.accentText,
      itemHoverColor: BrandDarkColors.accentTextHover,
      itemColor: BrandDarkColors.muted,
    },
    Menu: {
      ...antdTheme.components?.Menu,
      itemBg: "transparent",
      itemColor: BrandDarkColors.muted,
      itemHoverColor: BrandDarkColors.ink,
      itemHoverBg: "rgba(255, 255, 255, 0.06)",
      itemSelectedColor: BrandDarkColors.ink,
      itemSelectedBg: BrandDarkColors.fill,
    },
    Segmented: {
      trackBg: BrandDarkColors.fill,
      itemSelectedBg: BrandDarkColors.surface,
      itemColor: BrandDarkColors.muted,
      itemHoverColor: BrandDarkColors.ink,
      itemSelectedColor: BrandDarkColors.ink,
    },
    Collapse: {
      headerBg: BrandDarkColors.fill,
      contentBg: BrandDarkColors.surface,
    },
  },
};

// The antd theme for a light/dark mode (see src/common/theme-mode). Dark exists only in the ADEED build;
// light is antdTheme above.
export const antdThemeFor = (mode) => (isAdeed && mode === ThemeMode.DARK ? adeedDarkAntdTheme : antdTheme);
